import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../styles/TrainingSummaryList.css";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import TrainingSummaryModal from "./TrainingSummaryModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import axios from "../../axiosConfig";
import {useToast} from "../commons/toast/ToastContext";

const TrainingSummaryList = ({ dogId, summaries }) => {
    const { t, i18n } = useTranslation('trainings');
    const [summaryList, setSummaryList] = useState(Array.isArray(summaries) ? summaries : []);
    const [error, setError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editSummaryId, setEditSummaryId] = useState(null);
    const [initialSummary, setInitialSummary] = useState("");
    const [initialRecommendations, setInitialRecommendations] = useState("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [summaryToDelete, setSummaryToDelete] = useState(null);

    // Initialize toast hook
    const toast = useToast();

    // ✅ Open Modal for Adding or Editing Summary
    const openModal = (summary = null) => {
        if (summary) {
            setEditSummaryId(summary.id);
            setInitialSummary(summary.summary);
            setInitialRecommendations(summary.recommendations);
        } else {
            setEditSummaryId(null);
            setInitialSummary("");
            setInitialRecommendations("");
        }
        setIsModalOpen(true);
    };

    // ✅ Save Summary (Add or Edit)
    const saveSummary = async (summary, recommendations) => {
        if (!summary.trim() || !recommendations.trim()) {
            toast.warning(t('summary.allFieldsRequired'), t('summary.missingData'));
            return;
        }

        try {
            const token = localStorage.getItem("jwt");

            if (editSummaryId) {
                // Edit Summary
                const response = await axios.put(`/api/v1/training-summary/edit/${editSummaryId}`, {
                    dogId,
                    summary,
                    recommendations,
                }, { headers: { Authorization: `Bearer ${token}` } });

                setSummaryList(summaryList.map(s => s.id === editSummaryId ? response.data : s));
                toast.success(t('summary.updated'), t('summary.success'));
            } else {
                // Add Summary
                const response = await axios.post("/api/v1/training-summary/add", {
                    dogId,
                    summary,
                    recommendations,
                }, { headers: { Authorization: `Bearer ${token}` } });

                setSummaryList([response.data, ...summaryList]);
                toast.success(t('summary.added'), t('summary.success'));
            }

            setIsModalOpen(false);
            setError(""); // Clear any previous errors
        } catch (err) {
            const errorMessage = editSummaryId
                ? t('summary.updateError')
                : t('summary.saveError');

            setError(errorMessage);
            toast.error(errorMessage, t('summary.error'));
        }
    };

    // ✅ Delete Summary
    const deleteSummary = async () => {
        if (!summaryToDelete) return;

        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/training-summary/${summaryToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setSummaryList(summaryList.filter(s => s.id !== summaryToDelete));
            setIsDeleteModalOpen(false);
            setError(""); // Clear any previous errors
            toast.success(t('summary.deleted'), t('summary.success'));
        } catch (err) {
            const errorMessage = t('summary.deleteError');
            setError(errorMessage);
            toast.error(errorMessage, t('summary.error'));
        }
    };

    return (
        <div className="trainer-summaries-container">
            {/* ✅ Add Summary Button */}
            <div className="add-summary-container">
                <button className="add-summary-btn-list" onClick={() => openModal()}>{t('summary.addSummary')}</button>
            </div>

            {/* ✅ Reusable Add/Edit Summary Modal */}
            <TrainingSummaryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={saveSummary}
                initialSummary={initialSummary}
                initialRecommendations={initialRecommendations}
                isEditing={!!editSummaryId}
            />

            {/* ✅ Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={deleteSummary}
                itemName={t('summary.summaryItem')}
            />

            {error && <p className="error">{error}</p>}

            {/* ✅ Summary List */}
            {summaryList.length === 0 ? <p>{t('summary.noSummaries')}</p> : (
                <div className="summary-list">
                    {summaryList.map((summary) => (
                        <div key={summary.id} className="summary-item">
                            <div className="summary-header">
                                <div className="summary-header-left">
                                    <p className="summary-date">{new Date(summary.createTime).toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US')}</p>
                                    <p className="summary-trainer">{summary.trainerName}</p>
                                </div>
                                <div className="summary-actions">
                                    <AiOutlineEdit className="training-summary-edit-icon" onClick={() => openModal(summary)} />
                                    <AiOutlineDelete className="training-summary-delete-icon" onClick={() => {
                                        setSummaryToDelete(summary.id);
                                        setIsDeleteModalOpen(true);
                                    }} />
                                </div>
                            </div>
                            <div className="summary-content">
                                <div className="summary-section">
                                    <p className="section-title">{t('summary.summaryLabel')}</p>
                                    <p className="section-text">{summary.summary}</p>
                                </div>
                                <div className="summary-section">
                                    <p className="section-title">{t('summary.recommendationsLabel')}</p>
                                    <p className="section-text">{summary.recommendations}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TrainingSummaryList;